/** @jsxImportSource @emotion/react */

import { FC, useMemo } from "react";
import {
  DocumentStages,
  DocumentStatus,
} from "../../../../graphql/__GENERATED__/types";
import useDocumentDetails from "../../../../hooks/useDocumentDetails";
import useDocumentOrder from "../../../../hooks/useDocumentOrder";
import PaymentInfo from "../sections/PaymentData";
import Quotations from "../sections/Quotations";
import Summary from "../sections/Summary";
import ArchivedProposals from "../sections/Proposal/ArchivedProposals";
import Claims from "../sections/Claim/sections/Claims";
import ComplementaryData from "../sections/Claim/sections/ComplementaryData";
import Inspections from "../sections/Claim/sections/Inspections";
import PolicyData from "../sections/Policy/PolicyData";
import Payments from "../sections/Policy/Payments";
import Sidebar from "../../../../components/Sidebar";
import DetailsMenu from "../../../../components/Details/components/Header/DetailsTabs";
import SignedProposal from "../sections/Proposal/SignedProposal";
import CurrentProposal from "../sections/Proposal/CurrentProposal";
import { ColorNames } from "../../../../theme";

const DocumentFlow: FC = () => {
  const {
    documentData: { stage, status },
  } = useDocumentDetails();

  const currentStage = useDocumentOrder(stage!, status!);
  const quotationStage = useDocumentOrder(DocumentStages.Simulation);
  const proposalStage = useDocumentOrder(DocumentStages.Proposal);
  const sentProposalStage = useDocumentOrder(
    DocumentStages.Proposal,
    DocumentStatus.Sent
  );
  const policyStage = useDocumentOrder(DocumentStages.Policy);
  const claimStage = useDocumentOrder(DocumentStages.Claim);

  const documentMenu = useMemo(() => {
    return [
      {
        title: "Geral",
        Component: Summary,
        color: ColorNames.GREEN,
      },
      {
        title: "Cotação",
        Component: Quotations,
        color: ColorNames.GREEN,
        disabled: currentStage < quotationStage,
      },
      {
        title: "Proposta",
        color: ColorNames.PINK,
        disabled: currentStage < proposalStage,
        tabs: [
          {
            title: "Emitida",
            Component: CurrentProposal,
          },
          {
            title: "Assinada",
            Component: SignedProposal,
          },
          {
            title: "Arquivadas",
            Component: ArchivedProposals,
          },
        ],
      },
      {
        title: "Pagamento",
        color: ColorNames.BLUE,
        disabled: currentStage < proposalStage,
        tabs: [
          {
            title: "Dados",
            Component: PaymentInfo,
          },
          {
            title: "Parcelas",
            Component: Payments,
          },
        ],
      },
      {
        title: "Apólice",
        color: ColorNames.BLUE,
        disabled: currentStage < sentProposalStage,
        tabs: [
          {
            title:
              status === DocumentStatus.Cancelled ? "Cancelada" : "Transmitida",
            Component: PolicyData,
          },
        ],
      },
      {
        title: "Sinistro",
        color: ColorNames.ORANGE,
        disabled: currentStage < policyStage,
        tabs: [
          {
            title: "Sinistros",
            Component: Claims,
          },
          {
            title: "Vistorias",
            Component: Inspections,
          },
          {
            title: "Dados",
            Component: ComplementaryData,
          },
        ],
      },
    ];
  }, [currentStage, quotationStage, proposalStage, policyStage, claimStage]);

  return <Sidebar TopComponent={DetailsMenu} menuItems={documentMenu} />;
};

export default DocumentFlow;
